<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  item: { type: Object, required: true },
  title: { type: String, required: true },
  address: { type: String, default: '' },
  ownerName: { type: String, default: '' },
  imageUrl: { type: String, default: '' },
  code: { type: String, default: '' },
  dueLabel: { type: String, default: 'Sem prazo' },
  statusLabel: { type: String, default: '' },
  urgencyLabel: { type: String, default: '' },
  state: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['pickup', 'return', 'actions', 'entry']);

const normalizedState = computed(() => props.state || props.item?.state || '');
const isToPickUp = computed(() =>
  ['to_pick_up', 'toPickUp'].includes(normalizedState.value)
);
const isToReturn = computed(() =>
  ['to_return', 'toReturn'].includes(normalizedState.value)
);

const statusClasses = computed(() => {
  if (isToReturn.value) return 'bg-rose-50 text-rose-700 border-rose-100';
  if (isToPickUp.value) return 'bg-amber-50 text-amber-700 border-amber-100';
  if (normalizedState.value === 'returned') {
    return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  }
  return 'bg-lais-primary-50 text-lais-primary border-lais-primary/10';
});

const primaryAction = computed(() => {
  if (isToPickUp.value) {
    return { label: 'Confirmar retirada', icon: 'i-lucide-check' };
  }
  if (isToReturn.value) {
    return { label: 'Confirmar devolução', icon: 'i-lucide-rotate-ccw' };
  }
  return { label: 'Mais ações', icon: 'i-lucide-more-horizontal' };
});

const runPrimary = () => {
  if (isToPickUp.value) {
    emit('pickup');
    return;
  }
  if (isToReturn.value) {
    emit('return');
    return;
  }
  emit('actions');
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <article
    class="grid gap-3 rounded-lais border border-lais-border bg-white p-3 shadow-lais-card sm:grid-cols-[112px_1fr] sm:p-4"
  >
    <div
      class="relative aspect-[7/4] overflow-hidden rounded-lais-sm bg-lais-bg-soft sm:aspect-auto sm:min-h-[96px]"
    >
      <img
        v-if="imageUrl"
        :src="imageUrl"
        :alt="title"
        class="size-full object-cover"
        loading="lazy"
      />
      <div
        v-else
        class="grid size-full min-h-[96px] place-items-center text-lais-primary"
      >
        <span class="i-lucide-building-2 size-7" aria-hidden="true" />
      </div>
      <span
        v-if="code"
        class="absolute bottom-2 left-2 rounded-full bg-white/95 px-2 py-0.5 text-[11px] font-semibold text-lais-text"
      >
        #{{ code }}
      </span>
    </div>

    <div class="grid min-w-0 gap-3">
      <div class="flex min-w-0 items-start justify-between gap-3">
        <div class="min-w-0">
          <h4 class="truncate text-[15px] font-semibold text-lais-text">
            {{ title }}
          </h4>
          <p
            v-if="address"
            class="mt-1 line-clamp-2 text-[12px] leading-5 text-lais-text-muted"
          >
            {{ address }}
          </p>
        </div>
        <span
          class="shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold"
          :class="statusClasses"
        >
          {{ statusLabel }}
        </span>
      </div>

      <dl class="grid gap-2 text-[12px] text-lais-text-body sm:grid-cols-2">
        <div class="rounded-lais-sm bg-lais-bg-soft px-3 py-2">
          <dt class="font-semibold text-lais-text-muted">Proprietário</dt>
          <dd class="mt-1 truncate font-semibold text-lais-text">
            {{ ownerName || 'Não informado' }}
          </dd>
        </div>
        <div class="rounded-lais-sm bg-lais-bg-soft px-3 py-2">
          <dt class="font-semibold text-lais-text-muted">Prazo</dt>
          <dd class="mt-1 font-semibold text-lais-text">
            {{ urgencyLabel || dueLabel }}
          </dd>
        </div>
      </dl>

      <div class="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <LaisPrimaryButton
          size="sm"
          :icon="primaryAction.icon"
          :is-loading="isLoading"
          @click="runPrimary"
        >
          {{ primaryAction.label }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          size="sm"
          variant="ghost"
          icon="i-lucide-door-open"
          @click="emit('entry')"
        >
          Instruções de entrada
        </LaisPrimaryButton>
        <LaisPrimaryButton
          size="sm"
          variant="ghost"
          icon="i-lucide-more-horizontal"
          @click="emit('actions')"
        >
          Mais ações
        </LaisPrimaryButton>
      </div>
    </div>
  </article>
</template>
